<template>
  <div>
    <p class="title">
      {{ challenge?.title }}
    </p>
    <hr />
    <p class="intro">
      Envoyer votre capture d'écran
      <span>avant le {{ date(challenge?.displayed_ends_at as string) }}</span>
      pour remporter
      <span
        >{{ challenge?.points }}
        {{ pluralize('point', challenge?.points) }}</span
      >.
    </p>
    <form
      autocomplete="off"
      @submit.prevent="submit"
    >
      <FilePreview
        basic
        required
        class="u-mb-4"
        @change="onImageChange"
      />
      <div class="c-form__group u-mb-4">
        <input
          id="pseudo"
          v-model="pseudo"
          type="text"
          name="pseudo"
          class="c-form__control"
          placeholder="Votre PSN ID*"
          required
        />
      </div>
      <div
        v-if="errorMessage"
        class="is-invalid u-mb-3"
      >
        {{ errorMessage }}
      </div>
      <div class="u-d-f u-jc-fe">
        <ButtonSimple
          type="submit"
          label="Envoyer"
          :disabled="disabledButton"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { RewardModalProps } from '~/types'
import type { CustomMedia } from '~/types/media'
import type { POSTReviewResponse } from '~/types/postReview'

const emit = defineEmits<{
  openRewardModal: [modalConfig: RewardModalProps]
}>()

const auth = useAuth()

const disabledButton = computed(() => {
  if (loading.value) return true
  return !pseudo.value || !image.value
})

const loading = ref(false)
const errorMessage = ref('')
const { gtag } = useGtag()
const route = useRoute()
const pseudo = ref(auth.data.value?.psn_id ?? '')
const image = ref('')
const challengesStore = useChallengesStore()
const { challenge } = storeToRefs(challengesStore)

const submit = async () => {
  loading.value = true
  errorMessage.value = ''

  gtag('event', 'popup_releve_challenge', {
    cta_name: 'Envoyer',
  })

  const { data, error } = await useCustomFetch<POSTReviewResponse>(
    `${API.POST__CHALLENGE}/${route.params.challengeName}/user`,
    {
      method: 'POST',
      body: {
        psnId: pseudo.value,
        screenshot: image.value,
      },
    },
  )

  const rawData = unref(data)
  if (error.value || !rawData) {
    if (error.value?.statusCode === 409) {
      errorMessage.value = 'Vous avez déjà participé à ce challenge.'
    }
    loading.value = false
    return console.error(
      'from challenge modal',
      'There was a problem submitting psnID and screenshot: ',
      error.value,
    )
  }

  await challengesStore.fetchChallenge({
    slug: route.params.challengeName as string,
  })

  const { badges_reward, ranking, points_total } = rawData

  emit('openRewardModal', {
    title: `Merci ${auth.data.value?.firstname}`,
    messages: [
      'Votre capture a été transmise aux équipes pour validation.',
      '<strong>Les challenges sont validés au fur et à mesure du mois.<br>Soyez patients 😉</strong>',
    ],
    badges: badges_reward,
    points: points_total,
    ranking,
    actions: [
      {
        type: 'route',
        route: '/challenges',
        label: 'Voir les autres challenges',
        tracking: {
          command: 'event',
          eventName: 'popup_releve_challenge',
          eventParams: {
            cta_name: 'Voir les autres challenges',
          },
        },
      },
    ],
    tracking: {
      command: 'event',
      eventName: 'popupPage',
      eventParams: {
        popupName: '/popup/challenge/confirmation',
      },
    },
    incoming: true,
  })

  loading.value = false
}

const onImageChange = (data: CustomMedia) => {
  if (data.preview) {
    image.value = data.preview
  }
}

const date = (value: string | number | Date) => {
  if (!value) return ''
  return format(new Date(value), 'dd/MM/yyy')
}
</script>

<style lang="scss" scoped>
hr {
  margin: 2.5rem 0;
  border-bottom: 0;
  opacity: 0.15;
}

.is-invalid {
  color: #ea5151;
}

.title {
  margin-bottom: 3rem;
  color: $blue;
  font-size: 2rem;
  line-height: 1.35;
}

.intro {
  margin-bottom: 3rem;

  span {
    color: $blue;
    font-weight: 700;
  }
}
</style>
